import { reactive } from "vue";
import router from "@/router";

// 花音改版页面里有两处会触发同一个"全屏菜单"、同一份"当前语言"：
// 一个是悬浮在封面图上的 header，一个是常驻在屏幕最下面的吸底工具栏。
// 两边点的其实是同一个开关，所以抽成一个共享的 reactive 单例，而不是
// 各自维护一份本地 state——否则从吸底栏点 MENU，header 里的抽屉不会跟着开。
//
// 没有直接塞进 Vuex(store/store.js)：那边是业务表单数据，每次 mutation 都会把
// 整份 state 存进 sessionStorage，混进这种纯 UI 开关状态不干净，用一个
// reactive() 单例更直接、也不会污染那份持久化的业务数据。
export const kanonUI = reactive({
  showDrawer: false,
  // 首页一大堆"预约/Book now"按钮（hero、header抽屉、各个banner、吸底工具栏……）
  // 点的都是同一个弹窗——先选日期/时间/人数，弹窗里真正的 Book now 才跳预约页，
  // 所以这个开关也放共享单例里，跟 showDrawer 一个道理
  showBookingModal: false,
  selectedLanguage: sessionStorage.getItem("userLanguage") || "zh_CN",
});

export const langOptions = [
  { value: "zh_CN", label: "简体中文" },
  { value: "zh_TW", label: "繁体中文" },
  { value: "en_US", label: "English" },
  { value: "ja_JP", label: "日本語" },
  { value: "ko_KR", label: "한국어" },
];

export function setLanguage(i18n, value) {
  kanonUI.selectedLanguage = value;
  i18n.locale = value;
  sessionStorage.setItem("userLanguage", value);
}

// 全屏菜单抽屉、预约弹窗都要在打开时把背景锁死（position:fixed + 记住滚动位置，
// 关闭时还原），原来这段逻辑写死在 KanonHeader 里，现在两处都要用，抽成共享函数。
// 用计数器而不是简单布尔值，是为了防止万一两个开合状态前后脚触发时，
// 后关闭的那个把还没关的另一个也误解锁了。
let scrollLockCount = 0;
let lockedScrollY = 0;

export function lockBodyScroll() {
  if (scrollLockCount === 0) {
    lockedScrollY = window.scrollY;
    const { style } = document.body;
    style.position = "fixed";
    style.top = `-${lockedScrollY}px`;
    style.left = "0";
    style.right = "0";
    style.width = "100%";
  }
  scrollLockCount++;
}

export function unlockBodyScroll() {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0) {
    const { style } = document.body;
    style.position = "";
    style.top = "";
    style.left = "";
    style.right = "";
    style.width = "";
    window.scrollTo(0, lockedScrollY);
  }
}

// header 抽屉菜单、footer 底部导航都有一批"点了滚到首页某个板块"的链接
// （aboutKanon/reviews/plan/hairMakeup/process...），原来两边各自都是简单一句
// document.getElementById(id)?.scrollIntoView(...)——这在首页上没问题，但预约页
// 这类二级页面根本没有这些 id 对应的板块，点了自然什么反应都没有。
// 这里统一收成一个函数：先判断在不在首页，不在的话先跳转回首页，
// 等首页那个懒加载组件真正渲染出来、目标元素出现了，再滚过去
export function goToHomeSection(id) {
  const scrollToTarget = () => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  if (router.currentRoute.value.path === "/") {
    scrollToTarget();
    return;
  }

  router.push("/").then(() => {
    // HomeIndex 是路由懒加载的组件，push 这个 Promise resolve 时对应的 chunk
    // 不一定已经渲染完、目标元素不一定已经出现在 DOM 里——用短暂重试代替
    // 单次 nextTick，尽量兜住这段异步加载/渲染的时间差
    let tries = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      } else if (tries < 20) {
        tries++;
        setTimeout(tryScroll, 50);
      }
    };
    tryScroll();
  });
}
